import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Description as DocumentIcon,
  Assignment as NDAIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Science as ScienceIcon,
} from "@mui/icons-material";
import {
  ndaService,
  researchProjectService,
} from "../../services/api";

const AdminPanel = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [projectsWithNDAs, setProjectsWithNDAs] = useState([]);
  const [stats, setStats] = useState({ totalProjects: 0, totalNDAs: 0, totalSignatures: 0 });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError("");

      const [projectsRes, ndasRes] = await Promise.all([
        researchProjectService.getAllWithNDAs(),
        ndaService.getAll(),
      ]);

      const projects = projectsRes.data.projects || [];
      const ndas = ndasRes.data || [];
      setProjectsWithNDAs(projects);

      const totalSignatures = projects.reduce(
        (acc, p) => acc + (p.ndaSignatures?.length || 0),
        0
      );

      setStats({
        totalProjects: projects.length,
        totalNDAs: ndas.length,
        totalSignatures,
      });
    } catch (err) {
      setError("Error al cargar datos del dashboard");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = (status) => {
    const map = {
      signed: "Firmado",
      pending_legal: "Pendiente legal",
      pending_upload: "Pendiente carga",
      rejected: "Rechazado",
      revoked: "Revocado",
    };
    return map[status] || status;
  };

  const statusColor = (status) => {
    const map = {
      signed: "success",
      pending_legal: "warning",
      pending_upload: "info",
      rejected: "error",
      revoked: "error",
    };
    return map[status] || "default";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString();
  };

  const TxHashLink = ({ hash }) => {
    if (!hash) return <Typography variant="caption" color="textSecondary">--</Typography>;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
          {hash.substring(0, 16)}...
        </Typography>
        <Button
          size="small"
          variant="text"
          startIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
          href={`https://polygonscan.com/tx/${hash}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: "0.65rem", p: 0, minWidth: "auto" }}
        >
          Ver
        </Button>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" component="h1">
              Panel de Administracion
            </Typography>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadDashboardData}>
              Actualizar
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ScienceIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4">{stats.totalProjects}</Typography>
                    <Typography variant="body2" color="textSecondary">Proyectos</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <NDAIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4">{stats.totalNDAs}</Typography>
                    <Typography variant="body2" color="textSecondary">NDAs</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <DocumentIcon color="info" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4">{stats.totalSignatures}</Typography>
                    <Typography variant="body2" color="textSecondary">Solicitudes de acceso</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Traceability */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <ScienceIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h5">Trazabilidad de Proyectos y NDAs</Typography>
          </Box>

          {projectsWithNDAs.length === 0 ? (
            <Alert severity="info">No hay proyectos registrados.</Alert>
          ) : (
            projectsWithNDAs.map((project) => (
              <Paper key={project.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                {/* Project Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, flexWrap: "wrap", gap: 1 }}>
                  <Box>
                    <Typography variant="h6">{project.title}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Investigador: <strong>{project.User?.name || "--"}</strong> ({project.User?.email || "--"})
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Creado: {formatDate(project.createdAt)} | Estado: {project.status || "--"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    {project.requiresNDA ? (
                      <Chip label="Requiere NDA" color="warning" size="small" variant="outlined" />
                    ) : (
                      <Chip label="Acceso libre" color="success" size="small" variant="outlined" />
                    )}
                    {project.AssociatedNDA ? (
                      <Chip
                        label={`NDA: ${project.AssociatedNDA.title}`}
                        color="info"
                        size="small"
                      />
                    ) : project.requiresNDA ? (
                      <Chip label="Sin NDA asociado" color="error" size="small" variant="outlined" />
                    ) : null}
                  </Box>
                </Box>

                {/* Project Blockchain Hash */}
                {project.blockchainTxHash && (
                  <Box sx={{ mb: 1, p: 1, backgroundColor: "rgba(46, 125, 50, 0.05)", borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Hash Blockchain del Proyecto:
                    </Typography>
                    <TxHashLink hash={project.blockchainTxHash} />
                  </Box>
                )}

                {/* Archivos del proyecto */}
                {project.projectFiles && project.projectFiles.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Archivos: {project.projectFiles.map(f => f.originalName || f.normalizedName).join(", ")}
                    </Typography>
                  </Box>
                )}

                {/* NDA Signatures / Access Requests */}
                {project.ndaSignatures && project.ndaSignatures.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                      Solicitudes de acceso ({project.ndaSignatures.length}):
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
                            <TableCell><strong>Solicitante</strong></TableCell>
                            <TableCell><strong>Estado</strong></TableCell>
                            <TableCell><strong>Fecha solicitud</strong></TableCell>
                            <TableCell><strong>NDA subido</strong></TableCell>
                            <TableCell><strong>Aprobacion legal</strong></TableCell>
                            <TableCell><strong>Hash Blockchain</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {project.ndaSignatures.map((sig) => (
                            <TableRow key={sig.id} hover>
                              <TableCell>
                                <Typography variant="body2">{sig.User?.name || "--"}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {sig.User?.email || "--"} ({sig.User?.role || "--"})
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={statusLabel(sig.status)}
                                  size="small"
                                  color={statusColor(sig.status)}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {formatDate(sig.createdAt) || "--"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {sig.userUploadedAt ? (
                                  <Typography variant="caption">
                                    {formatDate(sig.userUploadedAt)}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">--</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {sig.legalApprovalDate ? (
                                  <Typography variant="caption">
                                    {formatDate(sig.legalApprovalDate)}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">--</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <TxHashLink hash={sig.blockchainTxHash} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                    Sin solicitudes de acceso.
                  </Typography>
                )}
              </Paper>
            ))
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminPanel;
